<template>
  <section>
    <h1 v-if="!editable" class="title">{{ title }}</h1>
    <EditableText v-else class="title" v-model="title" :max-length="80" @onBlur="updateTitle" />

    <Button v-if="isDraftItinerary" @click="showModal = true" class="red" text="Share draft trip" />

    <Teleport to="body">
      <Dialog class="modal" v-if="showModal">
        <template #header>
          <div class="header">Share draft trip</div>
        </template>

        <div class="body">
          <div>We will review your itinerary within 24 hours. In the meantime, start sharing the draft trip.</div>

          <div>
            <div class="label">Link</div>
            <textarea rows="5" v-model="productLink"></textarea>
          </div>

          <div class="buttons">
            <Button text="Cancel" @click="showModal = false" />
            <Button
              v-if="isSupported"
              color="blue"
              :text="copied ? 'Copied' : 'Copy link'"
              @click="copy(productLink)"
            />
          </div>
        </div>
      </Dialog>
    </Teleport>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { Dialog } from '@/components/Theme'
import { useClipboard } from '@vueuse/core'
import Button from '@/components/Theme2/Button.vue'
import { useItineraryStore } from '@/stores/itinerary.js'
import EditableText from '../../components/EditableText.vue'
import trim from 'lodash/trim'

defineProps<{ editable: boolean }>()

const showModal = ref(false)
const {
  data: { itinerary, productUrl, isDraftItinerary },
  updateProduct,
} = useItineraryStore()
const productLink = computed(() => location.origin + productUrl)
const { copy, copied, isSupported } = useClipboard({ productLink })
const title = ref(trim(itinerary.title, ' -'))
const updateTitle = async (props: { field: 'title' | 'prelude'; value: string }) => {
  if (props.field !== 'title' && props.value.length > 80) {
    return
  }
  await updateProduct({ productId: itinerary.id, title: props.value })
}
</script>

<style lang="scss" scoped>
section {
  display: flex;
  justify-content: space-between;
}

.title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  line-height: 28px;

  @include mobile {
    line-height: 150%; /* 36px */
  }
}

.modal {
  @include base-font;

  .header {
    font-size: 24px;
    line-height: 28px;
    font-weight: 700;
  }

  .body {
    @include flex-column(24px);
  }

  .buttons {
    display: flex;
    gap: 24px;
    justify-content: center;
  }

  .label {
    color: $gray;
    font-size: 12px;
    line-height: 14px;
    font-weight: 700;
    text-transform: uppercase;
    margin-bottom: 8px;
  }

  textarea {
    font-size: 16px;
    line-height: 22px;
    width: 100%;
    border: 1px solid $gray-lighter;
    resize: none;
    padding: 12px 20px;
    outline: 0;
    word-break: break-all;
  }
}
</style>
